import { PerformanceMarkStartTime } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { useUserPerceivedLoadTimingWithKey } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { IRetailerBrandFollow } from "@faire/web-api--source/indigofair/data/IRetailerBrandFollow";
import * as React from "react";
import styled from "styled-components";

import { BannerPicture } from "@faire/retailer/views/brand/BrandHeader/BannerPicture";

import { FaireDirectSectionErrorFallback } from "../__internal__/FaireDirectSectionErrorFallback";
import { useSearchProductsFromBrandQuery } from "../__internal__/useSearchProductsFromBrandQuery/useSearchProductsFromBrandQuery";

import { BrandPreview } from "./BrandPreview";

const BANNER_IMAGE_HEIGHT = 200;

export const BrandPreviewWithBanner: React.FC<{
  brandToken: string;
  brand: IBrand;
  pageJsExecutedMark: PerformanceMarkStartTime | undefined;
}> = ({ brand, brandToken, pageJsExecutedMark }) => {
  const { data, isPending, isError, dataUpdatedAt } =
    useSearchProductsFromBrandQuery();

  const pageLoadKey = `page-faire-direct-landing-page-${brandToken}`;
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    name: "page-faire-direct-landing",
    pageLoadKey,
    dataUpdatedAt,
    numComponents: 2,
    isPageLoading: isPending,
    pageJsExecutedMark: pageJsExecutedMark,
    enableTrackPerformanceBreakdown: "internal-tracking",
    // This is needed because of the sign-up query param that immediately follows on landing.
    enablePageUpdateFix: true,
  });

  const brandPreview = IRetailerBrandFollow.build({
    brand_based_in_city: brand.based_in_city,
    brand_based_in_state: brand.based_in_state,
    brand_first_order_minimum: brand.first_order_minimum_amount,
    brand_name: brand.name,
    brand_overall_rating: brand.brand_reviews_summary?.average_rating,
    brand_profile_image: brand.profile_image,
    brand_reorder_minimum: brand.reorder_minimum_amount,
    brand_token: brand.token,
  });

  const productTiles: ILeanProductTile[] = data?.product_tiles ?? [];
  const totalProductCount = data?.pagination_data?.total_results;

  if (isError) {
    return <FaireDirectSectionErrorFallback />;
  }

  return (
    <>
      <BannerContainer ref={loadTimingRef}>
        <BannerPicture brandImage={brand.banner_image} />
      </BannerContainer>
      <BrandContainer>
        <BrandPreview
          brand={brand}
          item={brandPreview}
          totalProductCount={totalProductCount}
          productTiles={productTiles}
          loadTimingRef={loadTimingRef}
        />
      </BrandContainer>
    </>
  );
};

const BannerContainer = styled.div`
  width: 100%;
  height: ${BANNER_IMAGE_HEIGHT}px;
`;

const BrandContainer = styled.div`
  padding: 55px 55px 0;
  ${responsiveMediaQueries.tabletAndBelow} {
    padding: 32px 22px 0;
  }
`;
